import { Theme } from '@mui/material/styles';
import { darken, lighten } from '@mui/system';
import { colorToHex, hexToRgb } from '../../../../shared/utils/colors-utils';
import gaiaLogoWhite from '../../../../assets/corporate-logos/gaia/gaia-logo-white.png';
import aerialForest from '../../../../assets/cover-images/aerial-forest.jpg';

/**
 * White-label helpers for the auth screen.
 *
 * Nothing brand-specific is hardcoded in the components: the greens, logo, cover
 * photo, wash overlay and product name all resolve from the active partner theme
 * (CustomThemeContext), with the Gaia values below as the default fallback. See
 * design_handoff_auth_redesign/README.md -> "White-label & partner branding".
 */

// The exact Gaia wash from the design handoff. Used verbatim when no partner
// branding is active so the default screen matches the hifi design 1:1.
const GAIA_WASH =
  'linear-gradient(118deg, rgba(23,42,9,0.94) 0%, rgba(45,71,18,0.82) 38%, rgba(48,74,20,0.58) 72%, rgba(17,30,33,0.60) 100%)';

// Neutral dark ink (#111e21) fallback wash, used when a partner primary is too
// light / low-contrast to keep the reversed logo and headline legible over it.
const NEUTRAL_WASH =
  'linear-gradient(118deg, rgba(17,30,33,0.94) 0%, rgba(20,32,35,0.86) 38%, rgba(23,36,39,0.64) 72%, rgba(17,30,33,0.62) 100%)';

const INK = '#111e21'; // theme text.primary, the guaranteed-legible ink fallback

// Exact Gaia focus-ring token (--gaia-green-pale-light). Kept verbatim so the
// default screen matches the hifi design; partners derive a tint instead.
const GAIA_FOCUS_RING = '#e3ecca';

// Default Gaia editorial copy for the brand panel. Partners supply their own via
// /partner-settings; a partner who sets none simply has the editorial hidden.
// The headline newline is honoured with `whiteSpace: 'pre-line'` in the panel.
const GAIA_EYEBROW = 'Carbon accounting for real businesses';
const GAIA_HEADLINE = 'Every tonne,\naccounted for.';
const GAIA_SUBHEADLINE =
  'Measure, report and reduce your emissions in line with the GHG Protocol and SECR, all from a single, audit-ready platform.';

/**
 * Neutral (non-brand) design tokens from the handoff that have no MUI palette
 * equivalent. Kept here so all auth components share one source of truth.
 */
export const AUTH_TOKENS = {
  border: '#e2e4e7', // --gaia-border, hairlines
  inkSecondary: '#5a6568', // --gaia-ink-secondary, sub-text / labels (theme has no text.secondary)
  inkTertiary: '#8a9295', // --gaia-ink-tertiary, placeholders / leading icons
};

/**
 * Brand display stack, led by the Effra **Medium** cut (`EffraMd`) — the same
 * stack MUI uses for `typography.fontFamily`. Only Regular and Medium Effra
 * faces are loaded (there is no bold face), so prominent type must be pointed at
 * the real Medium cut; a plain `Typography` inherits `body1` (Effra Regular),
 * and `fontWeight: 600/700` on it only ever produces a synthetic (faux) bold.
 * Shared by the auth screens and the onboarding Welcome dialog.
 */
export const BRAND_DISPLAY_FONT = 'EffraMd,Effra,Roboto,"Helvetica Neue",Arial,sans-serif';

const toRgba = (color: string, alpha: number): string => {
  const { r, g, b } = hexToRgb(colorToHex(color));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** WCAG relative luminance (0 = black, 1 = white). */
const relativeLuminance = (color: string): number => {
  const { r, g, b } = hexToRgb(colorToHex(color));
  const channel = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** WCAG contrast ratio of a colour against white. */
const contrastOnWhite = (color: string): number => 1.05 / (relativeLuminance(color) + 0.05);

/**
 * Return a shade of `color` that clears 4.5:1 on white, darkening progressively
 * and falling back to ink. A no-op for colours that already pass (e.g. Gaia
 * primary.dark), so partner-only.
 */
const readableOnWhite = (color: string): string => {
  let candidate = colorToHex(color);
  for (let i = 0; i < 6 && contrastOnWhite(candidate) < 4.5; i += 1) {
    candidate = colorToHex(darken(candidate, 0.2));
  }
  return contrastOnWhite(candidate) >= 4.5 ? candidate : INK;
};

export interface AuthBranding {
  /** Display name that replaces "Gaia" in copy. */
  productName: string;
  /** Whether we are on default (unbranded) Gaia. */
  isDefaultGaia: boolean;
  /** Reversed/white logo for the photo column. */
  logoSrc: string;
  /**
   * Whether the logo needs a light plate behind it. Gaia ships a reversed white
   * wordmark that sits directly on the dark wash; a partner logo is not
   * guaranteed reversed (partner logos are dark-on-light by convention), so it
   * gets a light plate to stay legible.
   */
  logoNeedsPlate: boolean;
  /** Full-bleed cover photo behind the wash. */
  coverSrc: string;
  /** Green wash gradient over the photo, derived from the primary shades. */
  wash: string;
  /** Pale tint of primary used for the input focus ring. */
  focusRing: string;
  /** Primary shade guaranteed legible on the white trust pill (SECR mark). */
  secrInk: string;
  /** Whether the sign-up flow is available (partner may hide it). */
  allowSignup: boolean;
  /** Editorial copy for the brand panel (Gaia defaults, or partner-supplied). */
  eyebrow: string;
  headline: string;
  subheadline: string;
  /** Whether to show the editorial block (Gaia always; a partner only if they set any copy). */
  hasEditorialCopy: boolean;
}

/** Partner-supplied brand-panel overrides sourced from /partner-settings. */
export interface AuthBrandingOverrides {
  headline?: string;
  subheadline?: string;
  eyebrow?: string;
  reversedLogoUrl?: string;
}

/**
 * Resolve every brand-dependent value for the auth screen from the active theme
 * and partner config, falling back to Gaia defaults.
 */
export const resolveAuthBranding = (
  theme: Theme,
  partnerName: string,
  logoUrl: string,
  hideSignUpButton: boolean,
  overrides: AuthBrandingOverrides = {},
): AuthBranding => {
  const isDefaultGaia = !partnerName || partnerName === 'Gaia';
  const reversedLogoUrl = overrides.reversedLogoUrl || '';
  // Editorial copy: Gaia uses its marketing defaults; a partner uses whatever
  // they set (each element is hidden individually when empty).
  const eyebrow = isDefaultGaia ? GAIA_EYEBROW : overrides.eyebrow || '';
  const headline = isDefaultGaia ? GAIA_HEADLINE : overrides.headline || '';
  const subheadline = isDefaultGaia ? GAIA_SUBHEADLINE : overrides.subheadline || '';
  const hasEditorialCopy = isDefaultGaia || Boolean(headline || subheadline || eyebrow);
  const primary = theme?.palette?.primary?.main || '#70942c';
  const primaryDark = theme?.palette?.primary?.dark || darken(primary, 0.4);

  // A partner primary must be dark enough that white text over the mid-opacity
  // wash stops still clears ~4.5:1; white-on-solid reaches 4.5:1 only at
  // luminance <= 0.1833, so anything lighter routes to the neutral dark wash.
  const primaryDarkEnoughForWhiteText = relativeLuminance(primaryDark) <= 0.18;

  let wash: string;
  if (isDefaultGaia) {
    wash = GAIA_WASH;
  } else if (primaryDarkEnoughForWhiteText) {
    wash = `linear-gradient(118deg, ${toRgba(darken(primaryDark, 0.45), 0.94)} 0%, ${toRgba(
      primaryDark,
      0.82,
    )} 38%, ${toRgba(primaryDark, 0.58)} 72%, ${toRgba(INK, 0.6)} 100%)`;
  } else {
    wash = NEUTRAL_WASH;
  }

  // Focus ring: exact Gaia token by default; for partners a pale tint of a
  // legible base so it stays visible even when the partner primary is light.
  const focusRing = isDefaultGaia ? GAIA_FOCUS_RING : lighten(readableOnWhite(primary), 0.72);

  return {
    productName: isDefaultGaia ? 'Gaia' : partnerName,
    isDefaultGaia,
    // On the dark wash we need a reversed logo. Gaia ships a white wordmark. A
    // partner who uploaded a reversed logo gets it directly on the wash; one who
    // only has a standard (dark-on-light) logo gets it on a light plate.
    logoSrc: isDefaultGaia ? gaiaLogoWhite : reversedLogoUrl || logoUrl || gaiaLogoWhite,
    logoNeedsPlate: !isDefaultGaia && !reversedLogoUrl,
    coverSrc: aerialForest,
    wash,
    focusRing,
    eyebrow,
    headline,
    subheadline,
    hasEditorialCopy,
    // primary.dark is the Gaia SECR colour and already clears contrast; for a
    // light partner it darkens to stay legible on the white pill.
    secrInk: readableOnWhite(primaryDark),
    allowSignup: !hideSignUpButton,
  };
};
